import * as React from 'react';
import { useSelector } from 'react-redux';
import { ChatMessageContentContainer } from './containers/ChatMessageContentContainer';
import { useMessageContextMenu } from './hooks';
import { trackShowMessage } from './tracking';
import { ChatMessageChatbotGreeting } from '../../components/ChatMessageChatbotGreeting';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { MessageProvider } from '../MessageProvider/MessageProvider';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { ChatMessageOfferGalleryContainer } from '../ChatMessageOfferGalleryContainer';
import { ChatMessageLayout } from '../../components/ChatMessageLayout';
import { useMessageType } from './hooks/useMessageType';

type TChatMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
  isLastInChain: boolean;
  isFirstUserPhrase: boolean;
};

export const ChatMessageContainer: React.FC<TChatMessageContainerProps> = ({
  message,
  messageId,
  isLastInChain,
  isFirstUserPhrase,
}) => {
  const { status: messageStatus, communicationId, isDeleted, offers, isOutcoming, type: messageKind } = message;

  const chatId = useSelector(currentOpenChatIDSelector) || '';

  const messageRef = React.useRef<HTMLDivElement | null>(null);

  const { cancelLongPress } = useMessageContextMenu({ messageId, isDeleted, messageRef });

  const messageType = useMessageType(message);

  const onShow = React.useCallback(() => {
    const isChatbotOfferMessage = chatId === CHATBOT_ID && Boolean(offers?.length) && !isOutcoming;
    const attributes = isChatbotOfferMessage ? 'Horizontal_scroll ' : undefined;

    trackShowMessage({
      chatId,
      messageId,
      communicationId,
      attributes,
      offer: offers,
    });
  }, [chatId, messageId, communicationId, offers, isOutcoming]);

  const isChatbotGreetingMessage = messageKind === 'textAndImages';
  const isOfferGalleryMessage = offers && offers.length > 1;

  return (
    <MessageProvider
      message={message}
      messageId={messageId}
      chatId={chatId}
      messageType={messageType}
      isLastInChain={isLastInChain}
      isFirstUserPhrase={isFirstUserPhrase}
      messageRef={messageRef}
      cancelLongPress={cancelLongPress}
    >
      <ChatMessageLayout messageStatus={messageStatus} messageType={messageType} onShow={onShow}>
        {isChatbotGreetingMessage && <ChatMessageChatbotGreeting />}

        {!isChatbotGreetingMessage && isOfferGalleryMessage && <ChatMessageOfferGalleryContainer />}

        {!isChatbotGreetingMessage && !isOfferGalleryMessage && <ChatMessageContentContainer />}
      </ChatMessageLayout>
    </MessageProvider>
  );
};
